import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

/**
 * Renderiza documento jurídico (Termos/Privacidade/DPA/Termo Signatário)
 * carregando da API /api/legal/:type. Conteúdo é markdown — usamos
 * renderer inline minimalista pra não adicionar dep nova só pra isso.
 *
 * Cobre: # ## ###, **bold**, _italic_, [link](url), - lista, parágrafos.
 * Suficiente pra texto jurídico padrão. Se ficar complexo, trocar por
 * react-markdown.
 */

interface LegalDoc {
  id: string
  type: string
  version: string
  title: string
  content: string
  publishedAt: string
}

function renderMarkdown(md: string): JSX.Element[] {
  const out: JSX.Element[] = []
  const lines = md.split('\n')
  let listBuffer: string[] = []
  let paraBuffer: string[] = []

  function flushList(key: string) {
    if (listBuffer.length) {
      out.push(
        <ul key={key} className="list-disc pl-6 space-y-1 my-3 text-gray-700">
          {listBuffer.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(item) }} />
          ))}
        </ul>
      )
      listBuffer = []
    }
  }
  function flushPara(key: string) {
    if (paraBuffer.length) {
      const text = paraBuffer.join(' ').trim()
      if (text) out.push(<p key={key} className="text-gray-700 leading-relaxed my-2" dangerouslySetInnerHTML={{ __html: inline(text) }} />)
      paraBuffer = []
    }
  }
  function inline(s: string): string {
    return s
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
  }

  lines.forEach((line, i) => {
    const key = `l${i}`
    if (line.startsWith('### ')) {
      flushPara(key + 'p'); flushList(key + 'l')
      out.push(<h3 key={key} className="text-base font-semibold text-gray-900 mt-5 mb-2">{line.slice(4)}</h3>)
    } else if (line.startsWith('## ')) {
      flushPara(key + 'p'); flushList(key + 'l')
      out.push(<h2 key={key} className="text-lg font-semibold text-gray-900 mt-6 mb-2">{line.slice(3)}</h2>)
    } else if (line.startsWith('# ')) {
      flushPara(key + 'p'); flushList(key + 'l')
      out.push(<h1 key={key} className="text-2xl font-bold text-gray-900 mt-2 mb-4">{line.slice(2)}</h1>)
    } else if (line.startsWith('- ')) {
      flushPara(key + 'p')
      listBuffer.push(line.slice(2))
    } else if (line.trim() === '') {
      flushPara(key + 'p'); flushList(key + 'l')
    } else {
      flushList(key + 'l')
      paraBuffer.push(line)
    }
  })
  flushPara('end'); flushList('endL')
  return out
}

interface PlatformContact {
  dpoName: string | null
  dpoEmail: string | null
  privacyEmail: string | null
  companyLegalName: string | null
  companyCnpj: string | null
}

export default function LegalPage({ type }: { type: 'TERMS' | 'PRIVACY' | 'DPA' | 'SIGNER_TERMS' | 'RIPD' }) {
  const [doc, setDoc] = useState<LegalDoc | null>(null)
  const [contact, setContact] = useState<PlatformContact | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    fetch(`/api/legal/${type}`)
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(setDoc)
      .catch(err => setError(err.message || 'Falha ao carregar'))
      .finally(() => setLoading(false))
    fetch('/api/legal/platform')
      .then(r => r.ok ? r.json() : null)
      .then(setContact)
      .catch(() => null)
  }, [type])

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-indigo-600">PAKT</Link>
          <Link to="/" className="text-sm text-indigo-600 hover:text-indigo-800 transition">
            ← Voltar
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {loading && <p className="text-gray-500">Carregando...</p>}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
            Não foi possível carregar o documento: {error}
          </div>
        )}
        {doc && (
          <>
            <div className="mb-6 flex items-baseline justify-between">
              <h1 className="text-3xl font-bold text-gray-900">{doc.title}</h1>
              <span className="text-xs font-mono text-gray-400">
                v{doc.version} · publicado {new Date(doc.publishedAt).toLocaleDateString('pt-BR')}
              </span>
            </div>
            <article className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
              {renderMarkdown(doc.content)}
            </article>
            {doc.version.includes('placeholder') && (
              <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded text-sm">
                ⚠ Esta é uma versão placeholder. O texto definitivo será publicado após revisão jurídica.
              </div>
            )}
            <div className="mt-6 text-sm text-gray-600">
              Dúvidas? Veja a <Link to="/ajuda" className="text-indigo-600 hover:underline">Central de Ajuda</Link>
              {' '}ou escreva pra{' '}
              <a href="mailto:suporte@pakt.io" className="text-indigo-600 hover:underline">suporte@pakt.io</a>.
            </div>

            {/* Bloco institucional: DPO + canal privacy + DSR */}
            {contact && (contact.dpoEmail || contact.privacyEmail || contact.companyLegalName) && (
              <div className="mt-6 pt-6 border-t border-gray-200 text-xs text-gray-500 space-y-1">
                {contact.companyLegalName && (
                  <div>
                    Controlador: <strong>{contact.companyLegalName}</strong>
                    {contact.companyCnpj && <span> · CNPJ {contact.companyCnpj}</span>}
                  </div>
                )}
                {contact.dpoEmail && (
                  <div>
                    Encarregado de Proteção de Dados (DPO):
                    {contact.dpoName ? ` ${contact.dpoName} — ` : ' '}
                    <a href={`mailto:${contact.dpoEmail}`} className="text-indigo-600 hover:underline">{contact.dpoEmail}</a>
                  </div>
                )}
                {contact.privacyEmail && (
                  <div>
                    Canal de privacidade: <a href={`mailto:${contact.privacyEmail}`} className="text-indigo-600 hover:underline">{contact.privacyEmail}</a>
                  </div>
                )}
                <div>
                  Exercer direitos (LGPD Art. 18):
                  {' '}<Link to="/privacidade/exercer-direitos" className="text-indigo-600 hover:underline">formulário online</Link>
                </div>
                <div>
                  Lista de sub-operadores:
                  {' '}<Link to="/sub-processadores" className="text-indigo-600 hover:underline">/sub-processadores</Link>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
